import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import { StatusFilterCheckboxes } from '@/views/ContentView/PageTreeToolbar/StatusFilterCheckboxes';
import { ImportedPagesButton } from '@/views/ContentView/PageTreeToolbar/ImportedPagesButton';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { ToolbarHeader } from '@/components/ToolbarHeader';
import Styles from '@/assets/js/Styles';
import PageTreeBulkOperations from '@/views/ContentView/PageTreeToolbar/PageTreeBulkOperations';
import { SortSelector } from '@/components/SortSelector';
import { EventCategory, MainCategory, SubCategory } from '@/declarations/Category';
import { LocationSelector } from '@/components/LocationSelector';
import { PageTreeFilterValues } from '@/declarations/PageTreeFilterValues';
import { PageFolder } from '@/declarations/models/Folder';
import { PageFolderAutocomplete } from '@/views/ContentView/PageTree/PageFolderAutocomplete';
import { useStore } from '@/components/store/Store';
import { Page } from '@/declarations/models/Page';
import { useRecursiveSelectionState } from '@/hooks/useRecursiveSelectionState';
import { Api, BulkEditBody, GetPagesListForSiteQueryParams } from '@/services/Api';
import { useApi } from '@/hooks/useApi';
import { apiTimestampToDate } from '@/utils/dates';
import { deepCopy } from '@/utils/object';
import { pagePreviewUrl } from '@/utils/previewUrl';
import { Status } from '@/declarations/models/Status';
import { SortDirection, SortType } from '@/declarations/models/SortOption';
import CreatePageModal from '../../components/CreatePageModal';
import { ContentViewContentContext, ContentViewContext, ContentViewContextValue } from './ContentViewContext';
import Loader from '../../components/Loader';
import Container from '../../components/Container';
import PageTree from './PageTree/PageTree';
import FilterCategories from './FilterSubCategories';
import { IMPORTED_VIEW_PARAM, isImportedView } from './importedView';
import { hasNarrowingFilter } from './filterNarrowing';
import { chunk, IMPORTED_DELETE_CHUNK_SIZE } from './chunkIds';
import { announcePageTreeChange, usePageTreeChanges } from './pageTreeSync';

export type ContentViewSortProperty = keyof Pick<Page, 'title' | 'status' | 'updated_at'>;
export type ContentViewSortDirection = 'asc' | 'desc';

export interface ContentViewProps {
  contentContext: ContentViewContentContext;
}

/** The `message` a refused request carries, if the backend sent one. */
const backendMessage = (error: unknown): string | undefined =>
  typeof error === 'object' && error !== null && 'message' in error ? String(error.message) : undefined;

export { IMPORTED_VIEW_PARAM };
/** Window features make the browser open a separate window rather than a tab. */
export const IMPORTED_WINDOW_FEATURES = 'popup=yes,width=1400,height=900';

const defaultFilterValues: PageTreeFilterValues = {
  query: '',
  statuses: [],
  sub_categories: [],
  lastEditedPagesView: false,
  includePastEvents: true,
  location_ids: [],
};

export const ContentView: FC<ContentViewProps> = ({ contentContext }) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tComponents } = useTranslation('components');
  const { enqueueSnackbar } = useSnackbar();

  const { state } = useStore();
  const { selectedSiteLanguage } = state;
  const selectedSiteId = state.selectedSite?.id || 0;

  const isEvent = contentContext === ContentViewContentContext.EVENT;

  // The imported view lives in the URL so it can be opened in a new window and survive a reload.
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const showImported = isImportedView(searchParams);
  const setShowImported = useCallback(
    (value: boolean) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(IMPORTED_VIEW_PARAM, '1');
        } else {
          next.delete(IMPORTED_VIEW_PARAM);
        }
        return next;
      });
    },
    [setSearchParams],
  );
  const importedViewHref = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.set(IMPORTED_VIEW_PARAM, '1');
    return `${pathname}?${params.toString()}`;
  }, [pathname, searchParams]);

  const [eventParams, setEventParams] = useState<Partial<GetPagesListForSiteQueryParams>>({
    is_event: isEvent,
    order_by: SortType.EVENT_START,
    order: SortDirection.ASC,
    past_events: true,
    location_ids: [],
    sub_categories: [],
    main_category: undefined,
    // Imported pages are only ever returned when asked for by name (ADR-0013), so the view is a request parameter.
    status_list: showImported ? [Status.IMPORTED] : undefined,
  });

  // Counts the changes other windows announce (see pageTreeSync), so what depends on the page tree reloads.
  const [remoteChanges, setRemoteChanges] = useState<number>(0);

  // The button is only offered while there is something to place. Checked again whenever the imported view is left
  // or another window changes the tree, since placing or deleting pages is what empties the list.
  const [hasImportedPages, setHasImportedPages] = useState<boolean>(false);
  useEffect(() => {
    if (!selectedSiteId || showImported) {
      return undefined;
    }
    // Only whether there are any: the smallest window the endpoint accepts, not the whole list
    const request = Api.getPagesListForSite(selectedSiteId, {
      is_event: isEvent,
      status_list: [Status.IMPORTED],
      rows: 5,
    });
    request.fetchDirect([] as Array<Page>).then((imported) => setHasImportedPages(imported.length > 0));
    return () => request.abort();
  }, [selectedSiteId, isEvent, showImported, remoteChanges]);

  const [pages, loadingPages, reloadPages, setPages] = useApi(
    () => Api.getPagesListForSite(selectedSiteId, eventParams),
    [] as Array<Page>,
  );

  const onRemoteChange = useCallback(() => {
    setRemoteChanges((count) => count + 1);
    reloadPages();
  }, [reloadPages]);
  usePageTreeChanges(selectedSiteId, onRemoteChange);

  const [pagesWithDraft] = useApi(() => Api.getPageIdsWithDraft(selectedSiteId), { page_ids: [] as Array<number> });

  const [lastEditedPages, setLastEditedPages] = useState<Array<Page>>([]);

  const [domain, loadingDomain, reloadDomain] = useApi(() => Api.getDomainForSite(selectedSiteId), null);

  const loading = loadingPages || loadingDomain;

  const [portalUrl, setPortalUrl] = useState<string>('#');

  const [selectionMode, setSelectionMode] = useState<boolean>(false);
  const [movePagesMode, setMovePagesMode] = useState<boolean>(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkOperationInProgress, setBulkOperationInProgress] = useState(false);

  const [createPageModalOpen, setCreatePageModalOpen] = useState<boolean>(false);
  const [createPageRoot, setCreatePageRoot] = useState<Page | null>(null);
  const [markedPageIds, setMarkedPageIds] = useState<Array<number>>([]);
  useEffect(() => setMarkedPageIds([]), [selectedSiteId]);

  const [expandedPageIds, setExpandedPageIds] = useState<Array<string>>([]);
  const [filterValues, setFilterValues] = useState<PageTreeFilterValues>(defaultFilterValues);
  const [filterCategoriesVisible, _setFilterCategoriesVisible] = useState<boolean>(false);
  const setFilterCategoriesVisible = useCallback((value: boolean) => {
    _setFilterCategoriesVisible(value);
    if (!value) {
      setFilterValues((prev) => {
        return { ...prev, main_category: undefined, sub_categories: [] };
      });
    }
  }, []);
  useEffect(() => {
    setEventParams((prev) => {
      const wanted = showImported ? Status.IMPORTED : undefined;
      return prev.status_list?.[0] === wanted ? prev : { ...prev, status_list: wanted ? [wanted] : undefined };
    });
    setFilterValues((prev) => (prev.statuses.length ? { ...prev, statuses: [] } : prev));
    if (showImported) {
      // The imported view has neither status nor category filters.
      setFilterCategoriesVisible(false);
    }
  }, [showImported, setFilterCategoriesVisible]);

  const [resetSearch, setResetSearch] = useState<number>(0);
  const [sortProperty, setSortProperty] = useState<ContentViewSortProperty>('title');
  const [sortDirection, setSortDirection] = useState<ContentViewSortDirection>('asc');
  const sortFn = useCallback(
    (page1: Page, page2: Page, sortProp?: ContentViewSortProperty, sortDir?: ContentViewSortDirection): number => {
      if (isEvent) {
        // sorted by backend
        return 0;
      }
      let a: string | number;
      let b: string | number;

      switch (sortProp || sortProperty) {
        case 'status':
          a = (page1.status ? tCommon(`Status.${page1.status}`) : '').toLowerCase();
          b = (page2.status ? tCommon(`Status.${page2.status}`) : '').toLowerCase();
          break;
        case 'updated_at':
          a = apiTimestampToDate(page1.updated_at || '')?.getTime() || 0;
          b = apiTimestampToDate(page2.updated_at || '')?.getTime() || 0;
          break;
        case 'title':
        default:
          a = String(page1.title || '').toLowerCase();
          b = String(page2.title || '').toLowerCase();
          break;
      }

      let order = 0;
      if (a < b) {
        order = -1;
      } else if (a > b) {
        order = 1;
      }
      return (sortDir || sortDirection) === 'asc' ? order : -order;
    },
    [sortProperty, sortDirection, tCommon, isEvent],
  );
  const pagesForSelectedLanguage = useMemo<Array<Page>>(() => {
    if (filterValues.lastEditedPagesView) {
      return lastEditedPages.filter((page) => page.locale === selectedSiteLanguage);
    }
    return pages.filter((page) => page.locale === selectedSiteLanguage);
  }, [filterValues.lastEditedPagesView, lastEditedPages, pages, selectedSiteLanguage]);

  const selectionState = useRecursiveSelectionState<Page>(
    pagesForSelectedLanguage,
    (page) => page.id || 0,
    (page) => page.parent_id || null,
    true,
  );

  const { deselectAll } = selectionState;

  const [movePagesInFolderMode, setMovePagesInFolderMode] = useState<boolean>(false);
  const [moveTargetFolder, setMoveTargetFolder] = useState<PageFolder | null>(null);
  const confirmMoveToFolder = useCallback(async () => {
    if (!selectionState.selectedItems.length) return;
    setBulkOperationInProgress(true);
    try {
      const requests = selectionState.selectedItems.map((page) => {
        if (!page.id) return Promise.resolve(null);
        return Api.movePageToFolder(page.id, moveTargetFolder?.id).fetchDirect(null);
      });
      await Promise.all(requests);
      reloadPages();
      setMovePagesInFolderMode(false);
      setMoveTargetFolder(null);
    } finally {
      setBulkOperationInProgress(false);
    }
  }, [moveTargetFolder?.id, reloadPages, selectionState.selectedItems]);

  const [folderResponse, , reloadSiteFolders] = useApi(() => Api.getRootFolders(selectedSiteId), {
    folders: [] as PageFolder[],
  });

  const deletePage = useCallback(
    async (page: Page) => {
      if (!page?.id) {
        enqueueSnackbar(tCommon('error'), { variant: 'error' });
        return;
      }
      let deletedIdx = -1;
      let deletedElement: Page | null = null;
      setPages((prev) => {
        deletedIdx = prev.findIndex((p) => p.id === page.id);
        if (deletedIdx >= 0) {
          const cpy = [...prev];
          // eslint-disable-next-line prefer-destructuring
          deletedElement = cpy.splice(deletedIdx, 1)[0];
          return cpy;
        }
        return prev;
      });
      if (deletedElement) {
        const [, error] = await Api.deletePage(page.id).fetch();
        if (error) {
          enqueueSnackbar(tCommon('deleteFailed'), { variant: 'error' });
          setPages((prev: Array<Page>) => {
            const cpy = [...prev];
            cpy.splice(deletedIdx, 0, deletedElement as Page);
            return cpy;
          });
        } else {
          enqueueSnackbar(tCommon('deleteSuccess'), { variant: 'success' });
        }
      }
    },
    [enqueueSnackbar, setPages, tCommon],
  );

  const changePageStatus = useCallback(
    async (page: Page, newStatus: Status) => {
      if (!page.id) {
        enqueueSnackbar(tCommon('error'), { variant: 'error' });
        return;
      }
      const fullPage = await Api.getPage(page.id).fetchDirect(null);
      const pageCopy = deepCopy(fullPage);
      let savedPage: Page | null = null;
      if (!pageCopy) {
        enqueueSnackbar(tCommon('error'), { variant: 'error' });
        return;
      }
      pageCopy.status = newStatus;
      savedPage = await Api.savePage(pageCopy.site_id, pageCopy).fetchDirect(null);
      if (!savedPage) {
        enqueueSnackbar(tCommon('saveFailed'), { variant: 'error' });
      } else {
        setPages((prev) => {
          return (prev.map((p: Page) => {
            if (p.id === savedPage?.id) {
              return savedPage;
            }
            return p;
          }) || prev) as Array<Page>;
        });
        enqueueSnackbar(tCommon('saved'), { variant: 'success' });
      }
    },
    [enqueueSnackbar, setPages, tCommon],
  );

  const { getChildren, getParents } = selectionState;
  /** `pages` and every page under them, each once, parents before children. */
  const withDescendants = useCallback(
    (roots: Array<Page>): Array<Page> => {
      const seen = new Set<number>();
      const collected: Array<Page> = [];
      const visit = (page: Page) => {
        if (!page.id || seen.has(page.id)) return;
        seen.add(page.id);
        collected.push(page);
        getChildren(page.id).forEach(visit);
      };
      roots.forEach(visit);
      return collected.sort((a, b) => getParents(a.id || 0).length - getParents(b.id || 0).length);
    },
    [getChildren, getParents],
  );

  /** Runs bulk edits one after the other; returns the backend's refusal, if any. */
  const runBulkSteps = useCallback(
    (steps: Array<BulkEditBody>): Promise<unknown> =>
      steps.reduce<Promise<unknown>>(async (previous, step) => {
        const failure = await previous;
        if (failure) return failure;
        const [, error] = await Api.bulkEditPages(selectedSiteId, step).fetch();
        return error || null;
      }, Promise.resolve(null)),
    [selectedSiteId],
  );

  const publishImportedPages = useCallback(
    async (selected: Array<Page>) => {
      const all = withDescendants(selected);
      const ids = new Set(all.map((page) => page.id));
      const rootPageId = state.selectedSiteDefaultPageId;
      if (!all.length || !rootPageId) {
        enqueueSnackbar(tCommon('error'), { variant: 'error' });
        return;
      }
      // Pages whose parent is not published along with them go to the root of the site's tree; the rest keep their
      // parent, which is published before them since `all` lists parents first.
      const topmost = all.filter((page) => !page.parent_id || !ids.has(page.parent_id));
      const nested = all.filter((page) => !topmost.includes(page));
      const idsOf = (list: Array<Page>) => list.map((page) => page.id || 0);
      // An imported page leaves its status only by placement under a page in the tree, and the placement may publish
      // in the same write (ADR-0013). So each page is placed and published at once: nothing can interrupt a page
      // between the two. Nested pages need their parent in the tree first, hence a second call for them.
      const steps: Array<BulkEditBody> = [
        { item_ids: idsOf(topmost), item_type: 'page', parent_id: rootPageId, status: Status.PUBLISHED },
        ...(nested.length ? [{ item_ids: idsOf(nested), item_type: 'page' as const, status: Status.PUBLISHED }] : []),
      ];
      setBulkOperationInProgress(true);
      try {
        const failure = await runBulkSteps(steps);
        if (failure) {
          enqueueSnackbar(backendMessage(failure) || tCommon('saveFailed'), { variant: 'error' });
          reloadPages();
          return;
        }
        setPages((prev) => prev.filter((page) => !ids.has(page.id)));
        announcePageTreeChange(selectedSiteId);
        enqueueSnackbar(tComponents('ContentView.importedPagesPublished', { count: all.length }), {
          variant: 'success',
        });
      } finally {
        setBulkOperationInProgress(false);
      }
    },
    [
      withDescendants,
      state.selectedSiteDefaultPageId,
      selectedSiteId,
      runBulkSteps,
      enqueueSnackbar,
      reloadPages,
      setPages,
      tCommon,
      tComponents,
    ],
  );

  /** Deletes the chunks one after the other, stopping at the first refusal, which it returns. */
  const deleteChunksInOrder = useCallback(
    (chunks: Array<Array<number>>, onChunkDone: (itemIds: Array<number>) => void): Promise<unknown> =>
      chunks.reduce<Promise<unknown>>(async (previous, itemIds) => {
        const failure = await previous;
        if (failure) return failure;
        const refusal = await runBulkSteps([{ item_ids: itemIds, item_type: 'page', status: Status.DELETED }]);
        if (!refusal) onChunkDone(itemIds);
        return refusal;
      }, Promise.resolve(null)),
    [runBulkSteps],
  );

  const deleteImportedPages = useCallback(
    async (selected: Array<Page>, onProgress?: (done: number, total: number) => void) => {
      // Children first, so no page is deleted while it still has children.
      const all = withDescendants(selected).reverse();
      if (!all.length) {
        return;
      }
      const total = all.length;
      let done = 0;
      setBulkOperationInProgress(true);
      try {
        onProgress?.(0, total);
        const chunks = chunk(
          all.map((page) => page.id || 0),
          IMPORTED_DELETE_CHUNK_SIZE,
        );
        const onChunkDone = (itemIds: Array<number>) => {
          done += itemIds.length;
          setPages((prev) => prev.filter((page) => !itemIds.includes(page.id || 0)));
          onProgress?.(done, total);
        };
        const failure = await deleteChunksInOrder(chunks, onChunkDone);
        if (failure) {
          const stopped = tComponents('ContentView.importedPagesDeleteStopped', { done, total });
          enqueueSnackbar([backendMessage(failure), stopped].filter(Boolean).join(' '), { variant: 'error' });
          reloadPages();
          announcePageTreeChange(selectedSiteId);
          return;
        }
        announcePageTreeChange(selectedSiteId);
        enqueueSnackbar(tComponents('ContentView.importedPagesDeleted', { count: total }), { variant: 'success' });
      } finally {
        setBulkOperationInProgress(false);
      }
    },
    [withDescendants, selectedSiteId, deleteChunksInOrder, enqueueSnackbar, reloadPages, setPages, tComponents],
  );

  const previewPage = useCallback(
    async (page: Page) => {
      if (!page.id) {
        return;
      }
      // The list row lacks the site path and the version's timestamp the preview needs.
      const [fullPage, versions] = await Promise.all([
        Api.getPage(page.id).fetchDirect(null),
        Api.getPageVersions(page.id).fetchDirect({ versions: [] }),
      ]);
      if (!fullPage) {
        enqueueSnackbar(tCommon('error'), { variant: 'error' });
        return;
      }
      const activeVersion = versions.versions.find((version) => version.id === fullPage.active_version_id);
      window.open(pagePreviewUrl(fullPage, activeVersion?.created_at), 'portal');
    },
    [enqueueSnackbar, tCommon],
  );

  const openCreatePageModal = useCallback((parentPage?: Page) => {
    setCreatePageModalOpen((alreadyOpen) => {
      if (!alreadyOpen) {
        setCreatePageRoot(parentPage || null);
        return true;
      }
      return alreadyOpen;
    });
  }, []);

  const closeCreatePageModal = useCallback(() => {
    setCreatePageModalOpen(false);
    setCreatePageRoot(null);
  }, []);

  const toggleLastEditedPagesView = useCallback(() => {
    setFilterValues((prev) => ({
      ...defaultFilterValues,
      lastEditedPagesView: !prev.lastEditedPagesView,
    }));
    setResetSearch(resetSearch + 1);
  }, [resetSearch]);

  const setMainCategory = useCallback(
    (mainCategory?: MainCategory) => {
      setFilterValues((prev) => {
        return { ...prev, main_category: mainCategory };
      });
    },
    [setFilterValues],
  );

  const hasDraft = useCallback(
    (pageId: number) => {
      return pagesWithDraft.page_ids.includes(pageId);
    },
    [pagesWithDraft],
  );

  const [activePageFolder, setActivePageFolder] = useState<PageFolder | null>(null);

  const contextValue = useMemo<ContentViewContextValue>(
    () => ({
      contentContext,
      pages: pagesForSelectedLanguage,
      setPages,
      selectionState,
      selectionMode,
      setSelectionMode,
      movePagesMode,
      movePagesInFolderMode,
      setMovePagesMode,
      setMovePagesInFolderMode,
      toggleLastEditedPagesView,
      filterValues,
      setFilterValues,
      expandedPageIds,
      setExpandedPageIds,
      allExpanded: expandedPageIds.length === selectionState.itemCount,
      hasFilterApplied: hasNarrowingFilter(filterValues) || showImported,
      reloadPages,
      deletePage,
      changePageStatus,
      openCreatePageModal,
      closeCreatePageModal,
      markedPageIds,
      setMarkedPageIds,
      portalUrl,
      sortProperty,
      setSortProperty,
      sortDirection,
      setSortDirection,
      sortFn,
      hasDraft,
      activePageFolder,
      setActivePageFolder,
      siteFolders: folderResponse.folders,
      reloadSiteFolders,
      bulkOperationInProgress,
      setBulkOperationInProgress,
      bulkLoading,
      setBulkLoading,
      showImported,
      setShowImported,
      publishImportedPages,
      deleteImportedPages,
      previewPage,
    }),
    [
      contentContext,
      pagesForSelectedLanguage,
      setPages,
      selectionState,
      selectionMode,
      movePagesMode,
      movePagesInFolderMode,
      toggleLastEditedPagesView,
      filterValues,
      expandedPageIds,
      reloadPages,
      deletePage,
      changePageStatus,
      openCreatePageModal,
      closeCreatePageModal,
      markedPageIds,
      portalUrl,
      sortProperty,
      sortDirection,
      sortFn,
      hasDraft,
      activePageFolder,
      folderResponse.folders,
      reloadSiteFolders,
      bulkOperationInProgress,
      setBulkOperationInProgress,
      bulkLoading,
      setBulkLoading,
      showImported,
      setShowImported,
      publishImportedPages,
      deleteImportedPages,
      previewPage,
    ],
  );

  useEffect(() => {
    // Construct domain
    if (domain) {
      const protocol = window.location.protocol || 'https:';
      const host = domain.name || window.location.hostname;
      const path = domain.path || '';
      const url = new URL(`${protocol}//${host}/${path}/${selectedSiteLanguage || ''}`);
      setPortalUrl(url.href);
    } else {
      setPortalUrl('#');
    }
  }, [domain, selectedSiteLanguage]);

  // Reload when what the list is fetched for changes. Compared with the last request rather than skipping the first
  // run, so StrictMode's second run of the effects (and unrelated re-renders) don't fetch the same list again;
  // useApi does the first load itself.
  const pagesRequestKey = JSON.stringify([selectedSiteId, contentContext, eventParams]);
  const lastPagesRequestKey = useRef<string>(pagesRequestKey);
  useEffect(() => {
    if (lastPagesRequestKey.current === pagesRequestKey) {
      return;
    }
    const siteChanged = JSON.parse(lastPagesRequestKey.current)[0] !== selectedSiteId;
    lastPagesRequestKey.current = pagesRequestKey;
    if (!selectedSiteId) {
      return;
    }
    if (siteChanged) {
      // Another site's tree is not kept on screen while this one loads
      setPages([]);
      reloadDomain();
    }
    reloadPages();
  }, [pagesRequestKey, selectedSiteId, reloadPages, reloadDomain, setPages]);

  useEffect(() => {
    setLastEditedPages([...pages].sort((page1, page2) => sortFn(page1, page2, 'updated_at', 'desc')).slice(0, 10));
  }, [pages, sortFn]);

  useEffect(() => {
    setSelectionMode(false);
    setMovePagesMode(false);
  }, [contentContext]);

  useEffect(() => {
    if (!selectionMode) {
      deselectAll();
    }
  }, [selectionMode, deselectAll]);

  const frontpage = useMemo<Page | undefined>(() => {
    return (
      pages.find((page) => page.id === state.selectedSiteDefaultPageId) ||
      (state.selectedSiteDefaultPageId ? ({ id: state.selectedSiteDefaultPageId, title: '/' } as Page) : undefined)
    );
  }, [pages, state.selectedSiteDefaultPageId]);

  const handleSelectedLocationsChange = (value: number[]) => {
    setEventParams((prev) => {
      return { ...prev, location_ids: value };
    });
  };

  function sortEventCategoryByLabelName(a: SubCategory, b: SubCategory) {
    const aLabel = tCommon(`SubCategory.${a}`);
    const bLabel = tCommon(`SubCategory.${b}`);
    return aLabel.localeCompare(bLabel);
  }

  const setSubCategories = (subCategories: SubCategory[]) => {
    setFilterValues((prev) => {
      return { ...prev, sub_categories: subCategories };
    });
  };

  return (
    <ContentViewContext.Provider value={contextValue}>
      <Container
        p={6}
        fullWidth
        fullHeight
        column
        gap={1}
        top
        // The imported view is told apart from the ordinary page tree by its background, not by restyling the tree.
        // The sheet must grow with the list, or the grey ends at the fold.
        sx={showImported ? { backgroundColor: Styles.Colors.LIGHTEST_GREY, height: 'auto', minHeight: '100%' } : {}}>
        <ToolbarHeader
          query={filterValues.query}
          onQueryChange={(v) => setFilterValues((prev) => ({ ...prev, query: v }))}
          resetSearch={resetSearch}
          heading={
            <Typography variant='h3' component='h1' sx={{ fontSize: 30 }}>
              {tComponents(
                `ContentView.${contentContext === ContentViewContentContext.EVENT ? 'eventsTitle' : 'pagesTitle'}`,
              )}
            </Typography>
          }
          topRight={
            <Container gap={1}>
              {/* The imported view has no page creation and no "last edited" filter; its pages are not in the tree yet. */}
              {!showImported && (
                <Button
                  disabled={isEvent ? false : !state.selectedSiteDefaultPageId}
                  variant={isEvent ? 'contained' : 'outlined'}
                  color='success'
                  startIcon={<MaterialSymbol name='add' />}
                  onClick={() => (isEvent ? openCreatePageModal() : openCreatePageModal(frontpage))}
                  type='button'>
                  {tComponents(isEvent ? 'CreatePageModal.NewEvent' : 'CreatePageModal.NewPage')}
                </Button>
              )}
              {!showImported && (
                <Button
                  variant='outlined'
                  sx={{
                    px: 3,
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content',
                    backgroundColor: filterValues.lastEditedPagesView ? Styles.Colors.BLACK : Styles.Colors.LIGHT_GREY,
                    '&:hover': {
                      backgroundColor: filterValues.lastEditedPagesView
                        ? Styles.Colors.DARK_GREY
                        : Styles.Colors.LIGHTEST_GREY,
                    },
                    border: `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}`,
                  }}
                  color={filterValues.lastEditedPagesView ? 'primary' : 'secondary'}
                  onClick={() => toggleLastEditedPagesView()}
                  startIcon={<MaterialSymbol name='history_toggle_off' />}>
                  {tComponents('PageTreeToolbar.toggleLastEditedButtonLabel')}
                </Button>
              )}
              <Button
                variant='outlined'
                sx={{
                  px: 3,
                  whiteSpace: 'nowrap',
                  minWidth: 'fit-content',
                  backgroundColor: selectionMode ? Styles.Colors.BLACK : Styles.Colors.LIGHT_GREY,
                  '&:hover': {
                    backgroundColor: selectionMode ? Styles.Colors.DARK_GREY : Styles.Colors.LIGHTEST_GREY,
                  },
                  border: `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}`,
                }}
                color={selectionMode ? 'primary' : 'secondary'}
                onClick={() => setSelectionMode((prevState) => !prevState)}>
                {tComponents('PageTreeToolbar.toggleSelectionModeButtonLabel')}
              </Button>
            </Container>
          }
          bottomRight={
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                gap: 1,
                justifyContent: 'flex-end',
              }}>
              {isEvent && (
                <>
                  <LocationSelector
                    fullWidth
                    size='small'
                    sx={{
                      minWidth: 200,
                      maxWidth: 450,
                    }}
                    value={eventParams.location_ids || []}
                    onChange={handleSelectedLocationsChange}
                    siteId={selectedSiteId}
                  />

                  <FormControl
                    variant='outlined'
                    size='small'
                    sx={{
                      minWidth: '200px',
                      maxWidth: '300px',
                    }}>
                    <InputLabel>{tComponents('PageTreeToolbar.subCategoryLabel')}</InputLabel>
                    <Select
                      multiple
                      value={eventParams.sub_categories}
                      onChange={(e) =>
                        setEventParams((prev) => {
                          return { ...prev, sub_categories: e.target.value as SubCategory[] };
                        })
                      }
                      input={<OutlinedInput />}
                      renderValue={(selected) =>
                        selected.map((category) => tCommon(`SubCategory.${category}`)).join(', ')
                      }>
                      {Object.values(EventCategory)
                        .sort(sortEventCategoryByLabelName)
                        .map((category) => (
                          <MenuItem key={category} value={category}>
                            <Checkbox checked={eventParams.sub_categories!.includes(category)} />
                            <ListItemText primary={tCommon(`SubCategory.${category}`)} />
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </Box>
          }
          boxProps={{
            sx: {
              alignSelf: 'stretch',
            },
          }}
        />
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            direction: 'row',
            justifyContent: 'space-between',
          }}>
          {isEvent && (
            <Container left sx={{}}>
              <SortSelector
                availableOptions={[SortType.EVENT_START, SortType.TITLE, SortType.UPDATED_AT]}
                sortOption={{
                  sortBy: eventParams.order_by || SortType.EVENT_START,
                  order: eventParams.order || SortDirection.ASC,
                }}
                setSortOption={(sort) => {
                  setEventParams((prev) => {
                    return { ...prev, order_by: sort.sortBy, order: sort.order };
                  });
                }}
              />
            </Container>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: 1,
              justifyContent: 'end',
            }}>
            <Box
              sx={{
                display: 'flex',
              }}>
              {!showImported && (
                <StatusFilterCheckboxes
                  selected={filterValues.statuses}
                  onChange={(statuses) => setFilterValues((prev) => ({ ...prev, statuses }))}
                />
              )}
              {(showImported || hasImportedPages) && (
                <ImportedPagesButton
                  active={showImported}
                  isEvent={isEvent}
                  onOpenHere={() => setShowImported(true)}
                  onOpenInNewWindow={() => window.open(importedViewHref, '_blank', IMPORTED_WINDOW_FEATURES)}
                  onClose={() => setShowImported(false)}
                />
              )}
              {!isEvent && !showImported && (
                <Button
                  variant={filterCategoriesVisible ? 'contained' : 'outlined'}
                  color='secondary'
                  onClick={() => setFilterCategoriesVisible(!filterCategoriesVisible)}>
                  {filterCategoriesVisible && (
                    <MaterialSymbol
                      name='close'
                      sx={{
                        fontSize: 'large',
                      }}
                    />
                  )}
                  {tComponents('PageTreeToolbar.filterCategoriesButtonLabel')}
                </Button>
              )}
              {isEvent && (
                <FormControlLabel
                  control={
                    <Checkbox
                      disableRipple
                      checked={!eventParams.past_events}
                      onChange={(event, checked) => {
                        setEventParams((prev) => {
                          return { ...prev, past_events: !checked };
                        });
                      }}
                    />
                  }
                  label={tCommon(`Status.hide_past_events`)}
                  labelPlacement='end'
                />
              )}
            </Box>
            {selectionMode && (
              <PageTreeBulkOperations
                reloadPages={reloadPages}
                setBulkLoading={setBulkLoading}
                bulkOperationInProgress={bulkOperationInProgress}
                setBulkOperationInProgress={setBulkOperationInProgress}
              />
            )}
          </Box>
        </Box>
        <Container pb={4} mt={2} fullHeight fullWidth column top>
          {filterCategoriesVisible && (
            <FilterCategories
              mainCategory={filterValues.main_category}
              onChangeMainCategory={(category) => {
                setMainCategory(category);
                setSubCategories([]);
              }}
              subCategories={filterValues.sub_categories}
              onChangeSubCategories={setSubCategories}
            />
          )}
          {/* A reload keeps the tree (and what is open in it) on screen; the loader is only for the first load */}
          {loading && pages.length > 0 && !bulkLoading && (
            <LinearProgress color='secondary' sx={{ width: '100%' }} aria-label={tCommon('loading')} />
          )}
          {(loading && pages.length === 0) || bulkLoading ? <Loader /> : <PageTree />}
        </Container>
      </Container>
      <CreatePageModal
        open={createPageModalOpen}
        parentPage={createPageRoot}
        pageFolderId={activePageFolder?.id}
        closeCreatePageModal={closeCreatePageModal}
        setPages={setPages}
        contentContext={contentContext}
        onPageCreated={(page) => {
          // Only the latest created page is marked; a move marks all the moved pages
          if (page.id) setMarkedPageIds([page.id]);
        }}
      />
      {movePagesInFolderMode && (
        <Dialog
          open={movePagesInFolderMode}
          onClose={() => {
            setMovePagesInFolderMode(false);
            setMoveTargetFolder(null);
          }}>
          <Container
            fullWidth
            column
            sx={{
              p: 2,
              gap: 2,
              minWidth: '400px',
            }}>
            <Typography variant='h4'>{tComponents('PageTreeToolbar.MovePageToFolderDialog.Title')}</Typography>
            <PageFolderAutocomplete
              folders={folderResponse.folders}
              onChange={(targetFolder) => {
                if (targetFolder?.id) {
                  setMoveTargetFolder(targetFolder);
                } else {
                  setMoveTargetFolder(null);
                }
              }}
            />
            <Container>
              <Button onClick={() => setMovePagesInFolderMode(false)}>
                {tComponents('PageTreeToolbar.MovePageToFolderDialog.Cancel')}
              </Button>
              <Button color='primary' variant='contained' onClick={() => confirmMoveToFolder()}>
                {tComponents('PageTreeToolbar.MovePageToFolderDialog.ConfirmMove')}
              </Button>
            </Container>
          </Container>
        </Dialog>
      )}
    </ContentViewContext.Provider>
  );
};

export default ContentView;
